import React from 'react'
import { IconButton, Skeleton, Table, Typography, icons, theme } from 'cdh-data-portal-theme'
import type { TableRow } from 'cdh-data-portal-theme'
import type { DataGovernor } from '../../../../api/client'
import { businessObjectFriendlyName, hubFriendlyName } from '../DataGovernorsPage.static'
import type { useStyles } from '../DataGovernorsPage.style'
import { COLUMNS, localText, removeGovernorAriaLabel } from './RegistryTable.static'

const SKELETON_ROW_COUNT = 5

export interface RegistryTableProps {
  readonly classes: ReturnType<typeof useStyles>['classes']
  readonly filteredGovernors: readonly DataGovernor[]
  readonly hubFriendlyNames: Readonly<Record<string, string>>
  readonly isLoading: boolean
  readonly onRemoveRequested: (governor: DataGovernor) => void
}

export const RegistryTable: React.FC<RegistryTableProps> = ({
  classes,
  filteredGovernors,
  hubFriendlyNames,
  isLoading,
  onRemoveRequested,
}) => {
  const tableRows: TableRow[] = filteredGovernors.map((governor) => ({
    rowKey: `${governor.email}:${governor.idp}:${governor.hub}:${governor.business_object}`,
    content: [
      <Typography key="email" variant="body">
        {governor.email}
      </Typography>,
      <Typography key="hub" variant="body">
        {hubFriendlyName(governor.hub, hubFriendlyNames)}
      </Typography>,
      <Typography key="businessObject" variant="body">
        {businessObjectFriendlyName(governor.business_object)}
      </Typography>,
      <IconButton
        key="remove"
        ariaLabel={removeGovernorAriaLabel(governor.email)}
        icon={icons.trash}
        onClick={() => onRemoveRequested(governor)}
        tooltip={localText.removeGovernorTooltip}
        variant="alert"
      />,
    ],
    sortValues: {
      email: governor.email,
      hub: governor.hub,
      businessObject: governor.business_object,
    },
  }))

  return (
    <Table
      className={classes.table}
      columnsTemplate={COLUMNS}
      emptyCard={
        <div className={classes.emptyState}>
          <Typography variant="body" color={theme.textColors.primaryLighter}>
            {localText.emptyStateMessage}
          </Typography>
        </div>
      }
      noResults={!isLoading && filteredGovernors.length === 0}
      paginationPosition="bottom"
      rows={isLoading ? buildSkeletonRows() : tableRows}
      rowsPerPage={15}
    />
  )

  function buildSkeletonRows(): TableRow[] {
    return Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => ({
      rowKey: `skeleton-${rowIndex}`,
      content: COLUMNS.map((_, columnIndex) => <Skeleton key={columnIndex} height={24} width="100%" />),
    }))
  }
}
